import React, {useEffect, useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {useTranslation} from "react-i18next";
import {Check, Filter, Loader2, Package, Search, Tag, X} from "lucide-react";
import Button from "../../Common/Button";
import {fetchCategories, searchProducts} from "./productStatsApi";

// Bouton « Produits / Catégories » de la barre d'Activité + panneau de
// sélection multiple. La sélection n'est appliquée qu'au clic sur
// « Appliquer » (brouillon local), pour ne pas relancer les stats à chaque case.

const useDebounced = (value, delay = 250) => {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return v;
};

const keyOf = (s) => `${s.type}:${s.id}`;

export const SelectionChips = ({selection, onRemove, isDarkMode, max = Infinity}) => {
    const {t} = useTranslation("activite");
    const shown = selection.slice(0, max);
    const rest = selection.length - shown.length;
    return (
        <div className="flex flex-wrap items-center gap-1.5">
            {shown.map((s) => {
                const Icon = s.type === "category" ? Tag : Package;
                return (
                    <span key={keyOf(s)}
                          className={`inline-flex max-w-[220px] items-center gap-1 py-1 pl-2.5 pr-1 text-xs font-medium ${
                              s.type === "category"
                                  ? "bg-violet-500/10 text-violet-700 dark:text-violet-300"
                                  : "bg-primary/10 text-primary"}`}>
                        <Icon className="h-3 w-3 flex-shrink-0"/>
                        <span className="truncate">{s.name}</span>
                        {onRemove && (
                            <button type="button" onClick={() => onRemove(s)}
                                    aria-label={t("productFilter.remove", {name: s.name})}
                                    className={`flex h-5 w-5 items-center justify-center ${
                                        isDarkMode ? "hover:bg-white/10" : "hover:bg-black/10"}`}>
                                <X className="h-3 w-3"/>
                            </button>
                        )}
                    </span>
                );
            })}
            {rest > 0 && <span className="text-xs text-gray-500">+{rest}</span>}
        </div>
    );
};

const Row = ({checked, onToggle, title, subtitle, color, isDarkMode}) => (
    <li>
        <button type="button" role="checkbox" aria-checked={checked} onClick={onToggle}
                className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                    checked ? "bg-primary/10" : isDarkMode ? "hover:bg-gray-700/60" : "hover:bg-gray-100"}`}>
            <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center border-2 ${
                checked ? "border-primary bg-primary text-white" : isDarkMode ? "border-gray-500" : "border-gray-300"}`}>
                {checked && <Check className="h-3.5 w-3.5" strokeWidth={3}/>}
            </span>
            {color && <span className="h-3 w-3 flex-shrink-0" style={{background: color}}/>}
            <span className="min-w-0 flex-1">
                <span className={`block truncate text-sm font-medium ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}>{title}</span>
                {subtitle && <span className="block truncate text-xs text-gray-500">{subtitle}</span>}
            </span>
        </button>
    </li>
);

export const ProductFilterPanel = ({value, onApply, onClose, isDarkMode}) => {
    const {t} = useTranslation("activite");
    const [draft, setDraft] = useState(value);
    const [tab, setTab] = useState("product");
    const [query, setQuery] = useState("");
    const term = useDebounced(query);

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const productsQuery = useQuery({
        queryKey: ["activity-filter-products", term],
        queryFn: () => searchProducts(term),
        enabled: tab === "product",
        staleTime: 60_000,
    });
    const categoriesQuery = useQuery({
        queryKey: ["activity-filter-categories"],
        queryFn: fetchCategories,
        staleTime: 5 * 60_000,
    });

    const selectedKeys = useMemo(() => new Set(draft.map(keyOf)), [draft]);
    const toggle = (item) => setDraft((prev) => (selectedKeys.has(keyOf(item))
        ? prev.filter((s) => keyOf(s) !== keyOf(item))
        : [...prev, item]));

    const categories = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = categoriesQuery.data || [];
        return q ? list.filter((c) => c.name.toLowerCase().includes(q)) : list;
    }, [categoriesQuery.data, query]);

    const activeQuery = tab === "product" ? productsQuery : categoriesQuery;
    const items = tab === "product" ? (productsQuery.data || []) : categories;
    const count = (type) => draft.filter((s) => s.type === type).length;

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 sm:items-center sm:p-4"
             onClick={onClose}>
            <div role="dialog" aria-modal="true" aria-label={t("productFilter.title")} onClick={(e) => e.stopPropagation()}
                 className={`flex max-h-[90dvh] w-full flex-col overflow-hidden border shadow-xl sm:max-w-[520px] ${
                     isDarkMode ? "border-gray-700 bg-gray-800 text-gray-100" : "border-gray-200 bg-white text-gray-900"}`}
                 style={{fontSize: "14px"}}>
                <div className={`flex items-center gap-2 border-b px-4 py-3 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                    <Filter className="h-4 w-4 text-primary"/>
                    <h3 className="flex-1 text-base font-semibold">{t("productFilter.title")}</h3>
                    <button type="button" onClick={onClose} aria-label={t("productFilter.close")}
                            className={`flex h-8 w-8 items-center justify-center text-gray-500 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}>
                        <X className="h-4 w-4"/>
                    </button>
                </div>

                <div role="tablist" className={`grid grid-cols-2 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                        {[["product", t("productFilter.products"), Package], ["category", t("productFilter.categories"), Tag]].map(([key, label, Icon]) => (
                            <button key={key} type="button" role="tab" aria-selected={tab === key}
                                    onClick={() => setTab(key)}
                                    className={`-mb-px flex h-10 items-center justify-center gap-1.5 border-b-2 text-sm transition ${
                                        tab === key ? "border-primary text-primary" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
                                <Icon className="h-4 w-4"/> {label}
                                {count(key) > 0 && (
                                    <span className="min-w-[18px] bg-primary px-1.5 text-[11px] text-white">{count(key)}</span>
                                )}
                            </button>
                        ))}
                </div>
                <div className="space-y-3 px-4 pt-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"/>
                        <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                               placeholder={tab === "product" ? t("productFilter.searchProducts") : t("productFilter.searchCategories")}
                               className={`h-10 w-full border pl-9 pr-3 text-sm outline-none focus:border-primary ${
                                   isDarkMode ? "border-gray-600 bg-gray-700 placeholder:text-gray-400" : "border-gray-200 bg-white placeholder:text-gray-400"}`}/>
                    </div>
                    {draft.length > 0 && (
                        <SelectionChips selection={draft} onRemove={toggle} isDarkMode={isDarkMode}/>
                    )}
                </div>

                <ul className={`mt-3 min-h-[200px] flex-1 divide-y overflow-y-auto overscroll-contain border-t ${
                    isDarkMode ? "divide-gray-700 border-gray-700" : "divide-gray-100 border-gray-200"}`}>
                    {activeQuery.isLoading && (
                        <li className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
                            <Loader2 className="h-4 w-4 animate-spin"/> {t("productFilter.loading")}
                        </li>
                    )}
                    {!activeQuery.isLoading && items.map((item) => {
                        const entry = {type: tab, id: item.id, name: item.name};
                        return (
                            <Row key={item.id} checked={selectedKeys.has(keyOf(entry))} onToggle={() => toggle(entry)}
                                 title={item.name} isDarkMode={isDarkMode}
                                 color={tab === "category" ? item.color : null}
                                 subtitle={tab === "product"
                                     ? [item.categoryName, item.variantsCount ? t("productFilter.variants", {count: item.variantsCount}) : null]
                                         .filter(Boolean).join(" · ")
                                     : null}/>
                        );
                    })}
                    {!activeQuery.isLoading && items.length === 0 && (
                        <li className="py-10 text-center text-sm text-gray-500">
                            {activeQuery.error ? t("productFilter.loadError") : t("productFilter.noResult")}
                        </li>
                    )}
                </ul>

                <div className={`flex gap-2 border-t px-4 py-3 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                    <button type="button" onClick={() => setDraft([])} disabled={draft.length === 0}
                            className={`h-10 flex-1 border text-sm font-medium disabled:opacity-40 ${
                                isDarkMode ? "border-gray-600 text-gray-100 hover:bg-gray-700" : "border-gray-200 text-gray-700 hover:bg-gray-50"}`}>
                        {t("productFilter.clear")}
                    </button>
                    <button type="button" onClick={() => onApply(draft)}
                            className="h-10 flex-1 bg-primary text-sm font-medium text-white hover:bg-primary-dark">
                        {draft.length ? t("productFilter.applyCount", {count: draft.length}) : t("productFilter.showAll")}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ProductFilterPicker = ({value, onChange, isDarkMode}) => {
    const {t} = useTranslation("activite");
    const [open, setOpen] = useState(false);
    const active = value.length > 0;
    return (
        <>
            {/* Même bouton que les autres actions de l'en-tête (Exporter…),
                teinté quand un filtre est actif. */}
            <Button variant={active ? "tinted" : "plain"} icon={Filter} compact aria-haspopup="dialog"
                    title={active ? value.map((s) => s.name).join(", ") : undefined}
                    onClick={() => setOpen(true)}>
                {active ? t("productFilter.buttonActive", {count: value.length}) : t("productFilter.button")}
            </Button>
            {open && (
                <ProductFilterPanel value={value} isDarkMode={isDarkMode} onClose={() => setOpen(false)}
                                    onApply={(next) => {
                                        onChange(next);
                                        setOpen(false);
                                    }}/>
            )}
        </>
    );
};

export default ProductFilterPicker;
